import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// Admin → Users: how many questions the name-check pop-up on Filing asks one person at each release (10:00, 13:00 and
// 16:00), and whether those releases open on Saturdays and Sundays for them too (report 114628af, 3rd opening).
// Everybody not listed keeps the standard allowance, Monday to Friday. People are picked from the staff logins, so no
// name is written into the code — the server keeps one setting per person (allowanceFor in server/src/routes/files.ts).
// The list is saved whole with Save, as Admin → Jobs' person pills are.

type Row = { userId: string; per: string; weekends: boolean };   // `per` as typed, so the box can be cleared and retyped
type Staff = { id: string; email: string; name: string; nickname: string; role: string; active: boolean };

const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 16, marginTop: 16 };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color,
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
});
const field: React.CSSProperties = {
  padding: '6px 8px', fontSize: 12.5, borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
};
const iconBtn: React.CSSProperties = { border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', padding: '2px 4px' };

export default function NameCheckAllowances() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [standard, setStandard] = useState(20);   // the server's own figures replace these on load
  const [max, setMax] = useState(100);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const take = (d: any) => {
    setRows((d?.people || []).map((p: any) => ({ userId: String(p.userId || ''), per: String(p.perWindow ?? ''), weekends: p.weekends === true })));
    if (Number(d?.standard) > 0) setStandard(Number(d.standard));
    if (Number(d?.max) > 0) setMax(Number(d.max));
  };
  useEffect(() => {
    Promise.all([api.adminGetNameCheckAllowances(), api.adminGetUsers()])
      .then(([d, u]: any[]) => {
        take(d);
        // Staff logins only: a client-portal or external login never sees the pop-up.
        setStaff((Array.isArray(u) ? u : []).filter((x: any) => x.role !== 'client' && x.role !== 'external'));
      })
      .catch((e: any) => setErr(e.message));
  }, []);

  const nameOf = (id: string) => { const u = staff.find(x => x.id === id); return u ? (u.nickname || u.name || u.email) : ''; };
  const edit = (next: (rs: Row[]) => Row[]) => { setDone(false); setErr(''); setRows(rs => next(rs || [])); };
  const set = (i: number, patch: Partial<Row>) => edit(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  // The server refuses the same lists (PUT /files/name-checks/settings); asked here first, as a refused call pages the admins.
  const problem = (): string => {
    const seen = new Set<string>();
    for (const r of rows || []) {
      if (!staff.some(u => u.id === r.userId)) return t('nca_need_person');
      if (seen.has(r.userId)) return t('nca_twice', { name: nameOf(r.userId) });
      seen.add(r.userId);
      const n = Number(r.per);
      if (!/^\d+$/.test(r.per.trim()) || n < 1 || n > max) return t('nca_bad_number', { name: nameOf(r.userId), max });
    }
    return '';
  };
  const save = async () => {
    const why = problem();
    if (why) { setErr(why); return; }
    setBusy(true); setErr('');
    try {
      take(await api.adminSetNameCheckAllowances((rows || []).map(r => ({ userId: r.userId, perWindow: Number(r.per), weekends: r.weekends }))));
      setDone(true);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <i className="ti ti-help-circle" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
        <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('nca_title')}</div>
        <button onClick={() => edit(rs => [...rs, { userId: '', per: String(standard), weekends: false }])} disabled={rows === null}
          style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), whiteSpace: 'nowrap' }}>
          <i className="ti ti-plus" aria-hidden /> {t('nca_add')}
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>{t('nca_desc', { n: standard })}</div>

      {rows === null ? (
        !err && <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /></div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '10px 0', borderTop: '1px solid var(--border)' }}>{t('nca_none', { n: standard })}</div>
      ) : rows.map((r, i) => (
        <div key={i} style={{ borderTop: '1px solid var(--border)', padding: '10px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select value={r.userId} onChange={e => set(i, { userId: e.target.value })} style={{ ...field, minWidth: 220 }}>
            <option value="">{t('nca_pick_person')}</option>
            {staff.filter(u => u.active || u.id === r.userId).map(u => (
              <option key={u.id} value={u.id}>{(u.nickname || u.name || u.email) + ' · ' + u.email}</option>
            ))}
          </select>
          <label title={t('nca_per_window_hint', { max })} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-primary)' }}>
            {t('nca_per_window')}
            <input type="number" min={1} max={max} step={1} value={r.per} onChange={e => set(i, { per: e.target.value })} style={{ ...field, width: 70 }} />
          </label>
          <label title={t('nca_weekends_hint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-primary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={r.weekends} onChange={e => set(i, { weekends: e.target.checked })} /> {t('nca_weekends')}
          </label>
          <div style={{ flex: 1 }} />
          <button onClick={() => edit(rs => rs.filter((_, j) => j !== i))} title={t('delete')} style={{ ...iconBtn, color: '#E24B4A' }}><i className="ti ti-trash" aria-hidden /></button>
        </div>
      ))}

      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 10 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        {done && <span style={{ fontSize: 12, color: '#15803D' }}>{t('nca_saved')}</span>}
        <button onClick={save} disabled={busy || rows === null} style={{ ...btn('var(--accent)'), opacity: busy || rows === null ? 0.6 : 1 }}>
          {busy ? t('login_wait') : t('save')}
        </button>
      </div>
    </div>
  );
}
